import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ExpertLevel, EXPERT_LEVEL_CONFIG, FeatureConfig } from '@/constants/expertModeConfig';

export type UIShell = 'clando' | 'msto' | 'rtm';

/** Preferences that only apply when the MSTO shell is active. */
export interface MstoPrefs {
    // General
    confirmBeforeDelete: boolean;
    addNewTasksToTop: boolean;
    moveStarredToTop: boolean;
    completionSound: boolean;
    // Smart list visibility
    showImportant: boolean;
    showPlanned: boolean;
    showAll: boolean;
    showCompleted: boolean;
    showAssigned: boolean;
    autoHideEmptySmartLists: boolean;
    // My Day composition
    includeTodayInMyDay: boolean;
}

const defaultMstoPrefs: MstoPrefs = {
    confirmBeforeDelete: true,
    addNewTasksToTop: true,
    moveStarredToTop: true,
    completionSound: true,
    showImportant: true,
    showPlanned: true,
    showAll: true,
    showCompleted: false,
    showAssigned: true,
    autoHideEmptySmartLists: false,
    includeTodayInMyDay: true,
};

interface Settings {
    accessibleTouchTargets: boolean;
    expertLevel: ExpertLevel;
    uiShell: UIShell;
    /** Visual resemblance of the MSTO shell to its reference palette, 0–100.
     *  100 = full reference palette (blue primary). 0 = Clando tokens only. */
    mstoIntensity: number;
    mstoPrefs: MstoPrefs;
    /** Visual resemblance of the RTM shell to its reference palette, 0–100.
     *  100 = warm red/orange primary. 0 = Clando tokens only. */
    rtmIntensity: number;
    // Granular feature overrides - can be expanded later
    featureOverrides?: Partial<FeatureConfig>;
}

interface SettingsContextType {
    settings: Settings;
    features: FeatureConfig;
    toggleAccessibleTouchTargets: () => void;
    setExpertLevel: (level: ExpertLevel) => void;
    setUIShell: (shell: UIShell) => void;
    setMstoIntensity: (value: number) => void;
    setRtmIntensity: (value: number) => void;
    updateMstoPref: <K extends keyof MstoPrefs>(key: K, value: MstoPrefs[K]) => void;
    updateFeatureOverride: (feature: keyof FeatureConfig, enabled: boolean) => void;
}

const SETTINGS_KEY = 'clando-settings';

const defaultSettings: Settings = {
    accessibleTouchTargets: false,
    expertLevel: 'easy',
    uiShell: 'clando',
    mstoIntensity: 100,
    mstoPrefs: defaultMstoPrefs,
    rtmIntensity: 100,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<Settings>(() => {
        const saved = localStorage.getItem(SETTINGS_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return {
                    ...defaultSettings,
                    ...parsed,
                    mstoPrefs: { ...defaultMstoPrefs, ...(parsed.mstoPrefs || {}) },
                };
            } catch (e) {
                console.error('Failed to parse settings:', e);
            }
        }
        return defaultSettings;
    });

    useEffect(() => {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }, [settings]);

    // Derived features based on expertLevel + overrides
    const features = useMemo(() => {
        const baseConfig = EXPERT_LEVEL_CONFIG[settings.expertLevel];
        return {
            ...baseConfig,
            ...settings.featureOverrides,
        };
    }, [settings.expertLevel, settings.featureOverrides]);

    const toggleAccessibleTouchTargets = () => {
        setSettings((prev) => ({
            ...prev,
            accessibleTouchTargets: !prev.accessibleTouchTargets,
        }));
    };

    const setExpertLevel = (level: ExpertLevel) => {
        setSettings((prev) => ({
            ...prev,
            expertLevel: level,
        }));
    };

    const setUIShell = (shell: UIShell) => {
        setSettings((prev) => ({
            ...prev,
            uiShell: shell,
        }));
    };

    const setMstoIntensity = (value: number) => {
        const clamped = Math.max(0, Math.min(100, Math.round(value)));
        setSettings((prev) => ({
            ...prev,
            mstoIntensity: clamped,
        }));
    };

    const setRtmIntensity = (value: number) => {
        const clamped = Math.max(0, Math.min(100, Math.round(value)));
        setSettings((prev) => ({
            ...prev,
            rtmIntensity: clamped,
        }));
    };

    const updateMstoPref = <K extends keyof MstoPrefs>(key: K, value: MstoPrefs[K]) => {
        setSettings((prev) => ({
            ...prev,
            mstoPrefs: { ...prev.mstoPrefs, [key]: value },
        }));
    };

    const updateFeatureOverride = (feature: keyof FeatureConfig, enabled: boolean) => {
        setSettings((prev) => ({
            ...prev,
            featureOverrides: {
                ...(prev.featureOverrides || {}),
                [feature]: enabled,
            },
        }));
    };

    return (
        <SettingsContext.Provider value={{
            settings,
            features,
            toggleAccessibleTouchTargets,
            setExpertLevel,
            setUIShell,
            setMstoIntensity,
            setRtmIntensity,
            updateMstoPref,
            updateFeatureOverride
        }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
